import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { theme } from 'components';
import {
  Modal,
  Alert,
  Button,
  Icon,
  Color,
  Flex,
  Result,
} from '@brokerbay/elements-web';
import styled from 'styled-components';
import { formatTimeStr, formatDateStrWithYearAndFullDayNoOrd } from 'utils';
import { showingInfoType } from './types';

type showingType =
  | 'autoConfirmWaiverUnneeded'
  | 'autoConfirmWaiverPending'
  | 'notAutoConfirm';

interface showingInfoModalProps {
  showModal: boolean;
  setShowModal: (newState: boolean) => void;
  apptInfo: string;
}
interface resultInfoMapItem {
  iconType: ReactNode;
  title: string;
  subtitle: string;
}
const ShowingInfoModal: React.FC<showingInfoModalProps> = ({
  showModal,
  setShowModal,
  apptInfo,
}) => {
  const showingInfo: showingInfoType = JSON.parse(
    atob(decodeURIComponent(apptInfo)),
  );
  // information in the modal
  const { apptId, isAutoConfirm, status } = showingInfo;
  const isCovidWaiverRequired =
    showingInfo.orgCovidIncluded && showingInfo.apptCovidRequired;
  const listingAddress = `${showingInfo.listingLine1}${showingInfo.listingLine2 ? ` ${showingInfo.listingLine2}` : ''}`;
  const showingDate = formatDateStrWithYearAndFullDayNoOrd(
    showingInfo.startTime,
    showingInfo.timezone,
  );
  const showingTime = `${formatTimeStr(
    showingInfo.startTime,
    showingInfo.timezone,
  )} - ${formatTimeStr(showingInfo.endTime, showingInfo.timezone)}`;
  const showingDuration = `${showingInfo.durationHours === 0 ? '' : `${showingInfo.durationHours} h `}${showingInfo.durationMin === 0 ? '' : `${showingInfo.durationMin}  min`}`;

  // different messages displayed for users based on covid waiver and auto-confirm statuses
  const resultInfoMap: Record<showingType, resultInfoMapItem> = {
    autoConfirmWaiverUnneeded: {
      iconType: <Icon icon="checkCircleFilled" color="success" size={50} />,
      title: 'Showing confirmed',
      subtitle:
        'Please make sure to view the showing details for further instructions.',
    },
    autoConfirmWaiverPending: {
      iconType: <Icon icon="clockFilled" color="warning" size={50} />,
      title: 'Showing pending',
      subtitle:
        'Please make sure to view the showing details for further instructions to confirm your showing.',
    },
    notAutoConfirm: {
      iconType: (
        <StyledSendIconBG>
          <Icon icon="sendOutlined" color="white" />
        </StyledSendIconBG>
      ),
      title: 'Showing request sent',
      subtitle: "You'll get notified once your request has been approved.",
    },
  };
  const decideShowingType = (
    autoConfirmStatus: boolean,
    showingStatus: string,
    WaiverRequiredStatus: boolean,
  ): showingType => {
    if (!autoConfirmStatus || showingStatus !== 'confirm') {
      return 'notAutoConfirm';
    }
    if (WaiverRequiredStatus) {
      return 'autoConfirmWaiverPending';
    }
    return 'autoConfirmWaiverUnneeded';
  };
  const type = decideShowingType(isAutoConfirm, status, isCovidWaiverRequired);
  const mappedResult = resultInfoMap[type];

  const handleClose = () => {
    setShowModal(false);
  };
  return (
    <StyledModal
      dataCy="showingInfo-modal"
      hideCloseIcon
      title=""
      open={showModal}
      onCancel={handleClose}
      width={400}
      footer={[
        <Flex key="wrapper" justify="space-between">
          <StyledCloseButton
            type="secondary"
            size="middle"
            key="back"
            onClick={handleClose}
            dataCy="showingInfoModal-close-button"
          >
            Close
          </StyledCloseButton>

          <StyledDetailsButton
            size="middle"
            key="link"
            type="primary"
            dataCy="showingInfoModal-details-button"
          >
            <Link to={`/my_business/showing/${apptId}?redirectTo=calendar`}>
              View showing details
            </Link>
          </StyledDetailsButton>
        </Flex>,
      ]}
    >
      <StyledResult
        title={mappedResult.title}
        subTitle={mappedResult.subtitle}
        icon={mappedResult.iconType}
      />
      <StyledAlert
        message={listingAddress}
        description={
          <>
            <StyledTimeInfoP>{showingDate}</StyledTimeInfoP>
            <StyledTimeInfoP>{showingTime}</StyledTimeInfoP>
            <StyledTimeInfoP>{showingDuration}</StyledTimeInfoP>
          </>
        }
        type="info"
      />
    </StyledModal>
  );
};

export default ShowingInfoModal;

const StyledModal = styled(Modal)`
  &&& .ant5-modal-body {
    padding-left: 24px;
    padding-right: 24px;
  }
  .ant5-modal-content,
  .ant5-modal-footer button {
    border-radius: ${theme.getBorderRadius('large')};
  }
  .ant5-modal-footer button:first-of-type {
    min-width: auto;
    width: 120px;
  }
  .ant5-modal-footer {
    border-top: 0 none;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 20px;
    &&& {
      display: 'flex';
      justify-content: 'space-between';
    }
  }
`;
const StyledResult = styled(Result)`
  padding-top: 0;
  padding-bottom: 10px;
  margin-bottom: 15px;
  .ant5-result-icon {
    margin-bottom: 18px;
  }
  .ant5-result-subtitle {
    color: ${Color.BLACK};
  }
`;
const StyledSendIconBG = styled.button.attrs({ disabled: true })`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  text-align: center;
  background-color: ${Color.PRIMARY};
  cursor: default;
  .anticon.anticon-send > svg {
    color: white;
    transform: rotate(-45deg);
    height: 24px;
    width: 24px;
    margin-right: -4px;
  }
`;
const StyledAlert = styled(Alert)`
  && {
    border: none;
    padding: 20px;
    border-radius: ${theme.getBorderRadius('large')};
  }
  .ant5-alert-message {
    font-weight: 500;
  }
`;
const StyledTimeInfoP = styled.p`
  margin-bottom: 5px;
  &&:first-of-type {
    margin-top: 5px;
  }
`;
const StyledCloseButton = styled(Button)`
  && {
    border-color: ${Color.BORDER_GREY};
    color: ${Color.BLACK};
  }
`;
const StyledDetailsButton = styled(Button)`
  & > a:hover {
    color: ${Color.WHITE};
  }
`;
//
